/**
 * 鱼缸服务
 * 处理鱼缸相关的 API 调用
 * 使用统一的 userApiClient 进行 API 调用
 *
 * @see Requirements 1.2 - 获取用户鱼缸列表
 */

import { userApiClient } from "@/lib/userApiClient";

// ============ 类型定义 ============

/** 鱼缸信息 */
export interface Tank {
  id: number;
  name: string;
  length: number;
  width: number;
  height: number;
  actual_water_level: number | null;
  volume: number;
  effective_volume: number;
  dimensions: string;
  filter_system: string;
  filter_system_text: string;
  location: string;
  location_text: string;
  water_type: string;
  water_type_text: string;
  pump_flow_rate: number | null;
  turnover_rate: number | null;
  active: boolean;
  created_at: string;
  updated_at: string;
  cover_image_url: string | null;
}

/** 过滤系统选项 */
export interface FilterSystemOption {
  value: string;
  label: string;
}

/** 位置选项 */
export interface LocationOption {
  value: string;
  label: string;
}

/** 水体类型选项 */
export interface WaterTypeOption {
  value: string;
  label: string;
}

/** 鱼缸列表响应 */
export interface TanksResponse {
  tanks: Tank[];
  total: number;
  filter_system_options: FilterSystemOption[];
  location_options: LocationOption[];
  water_type_options: WaterTypeOption[];
}

/**
 * 创建鱼缸请求参数
 * @see Requirements 5.3
 */
export interface CreateTankRequest {
  name: string;
  length: number;
  width: number;
  height: number;
  water_type: string;
  filter_system?: string;
  location?: string;
  actual_water_level?: number;
  pump_flow_rate?: number;
}

/**
 * 创建鱼缸响应
 * @see Requirements 5.3
 */
export interface CreateTankResponse {
  message: string;
  tank: Tank;
}

// ============ 服务类 ============

/**
 * 鱼缸服务类
 * 提供鱼缸相关的 API 操作
 */
class TankService {
  /**
   * 获取当前用户的所有鱼缸
   * @see Requirements 1.2
   *
   * @returns 鱼缸列表和选项
   */
  async getTanks(): Promise<TanksResponse> {
    return userApiClient.get<TanksResponse>("/tanks");
  }

  /**
   * 获取单个鱼缸详情
   *
   * @param id 鱼缸 ID
   * @returns 鱼缸详情
   */
  async getTank(id: number): Promise<Tank> {
    return userApiClient.get<Tank>(`/tanks/${id}`);
  }

  /**
   * 创建新鱼缸
   * @see Requirements 5.3
   *
   * @param data 鱼缸创建数据
   * @returns 创建结果和新鱼缸信息
   */
  async createTank(data: CreateTankRequest): Promise<CreateTankResponse> {
    return userApiClient.post<CreateTankResponse>("/tanks", { tank: data });
  }
}

// 导出单例
export const tankService = new TankService();
export default tankService;
